import React, { useEffect, useState, useRef } from 'react'
import { gql, useLazyQuery, useMutation } from '@apollo/client'
import { message } from 'antd'
import moment from 'moment'
import WithMainLayout from '../../hocs/withMainLayout'
import UsersTransactions from './UsersTransactions'

const GET_TRANSACTIONS = gql`
  query transactionsByUsers(
    $pagination: PaginationInput
    $filter: TransactionsFilterInput
  ) {
    transactionsByUsers(pagination: $pagination, filter: $filter) {
      transactions {
        id
        userId
        user {
          id
          name
          username
        }
        vendorTransactionId
        originalTransactionId
        transactionType
        plan
        expiresAt
        createdAt
        voided
        voidReason
      }
      total
      limit
      page
      pages
    }
  }
`

const EXPORT_TRANSACTIONS = gql`
  query transactionsExport($filter: TransactionsFilterInput) {
    transactionsExport(filter: $filter) {
      file
      rows
      total
      truncated
    }
  }
`

const VOID_TRANSACTION = gql`
  mutation voidTransaction($transactionId: ID!, $reason: String) {
    voidTransaction(transactionId: $transactionId, reason: $reason) {
      id
    }
  }
`

const LINK_TRANSACTION = gql`
  mutation linkTransactionToUser($originalTransactionId: String!, $userId: ID!) {
    linkTransactionToUser(originalTransactionId: $originalTransactionId, userId: $userId) {
      id
    }
  }
`

const ADD_MANUAL_TRANSACTION = gql`
  mutation addManualTransaction(
    $userId: ID!
    $plan: String!
    $months: Int!
    $comment: String
    $applyToSubscription: Boolean
  ) {
    addManualTransaction(
      userId: $userId
      plan: $plan
      months: $months
      comment: $comment
      applyToSubscription: $applyToSubscription
    ) {
      id
    }
  }
`

const toFilterInput = filters => ({
  search: filters.search || undefined,
  transactionType: filters.transactionType || undefined,
  dateFrom: filters.dateRange?.[0]
    ? filters.dateRange[0].format('DD.MM.YYYY')
    : undefined,
  dateTo: filters.dateRange?.[1]
    ? filters.dateRange[1].format('DD.MM.YYYY')
    : undefined
})

const UsersTransactionsContainer = () => {
  const [transactions, setTransactions] = useState([])
  const [filters, setFilters] = useState({
    search: '',
    transactionType: null,
    dateRange: null
  })
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(20)
  const [total, setTotal] = useState(0)
  const debounceRef = useRef(null)

  const [getTransactions, { loading }] = useLazyQuery(GET_TRANSACTIONS, {
    fetchPolicy: 'cache-and-network',
    onCompleted: data => {
      const updated = data.transactionsByUsers.transactions.map(t => ({
        ...t,
        expiresAt: t.expiresAt ? moment(t.expiresAt).format('DD.MM.YYYY') : '—',
        createdAt: t.createdAt ? moment(t.createdAt).format('DD.MM.YYYY') : '—'
      }))
      setTransactions(updated)
      setTotal(data.transactionsByUsers.total)
    },
    onError: () => {}
  })

  const fetchPage = (nextPage, nextLimit, nextFilters) => {
    getTransactions({
      variables: {
        pagination: { page: nextPage, limit: nextLimit },
        filter: toFilterInput(nextFilters)
      }
    })
  }

  useEffect(() => {
    fetchPage(1, limit, filters)
  }, [])

  useEffect(() => () => clearTimeout(debounceRef.current), [])

  const handleFiltersChange = partial => {
    clearTimeout(debounceRef.current)
    const next = { ...filters, ...partial }
    setFilters(next)
    setPage(1)
    if (partial.search !== undefined) {
      debounceRef.current = setTimeout(() => fetchPage(1, limit, next), 500)
    } else {
      fetchPage(1, limit, next)
    }
  }

  const handlePaginationChange = (nextPage, nextLimit) => {
    setPage(nextPage)
    setLimit(nextLimit)
    fetchPage(nextPage, nextLimit, filters)
  }

  const refetchCurrent = () => fetchPage(page, limit, filters)

  const [exportTransactions, { loading: exporting }] = useLazyQuery(
    EXPORT_TRANSACTIONS,
    {
      fetchPolicy: 'network-only',
      onCompleted: data => {
        const { file, rows, total, truncated } = data.transactionsExport
        const link = document.createElement('a')
        link.href =
          'data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,' +
          file
        link.download = `transactions_${moment().format('YYYY-MM-DD')}.xlsx`
        link.click()
        if (truncated) {
          message.warning(
            `Выгружено ${rows} строк из ${total} — под фильтр попало слишком много транзакций, сузьте диапазон дат`
          )
        } else {
          message.success(`Выгружено строк: ${rows}`)
        }
      },
      onError: () => message.error('Не удалось выгрузить транзакции')
    }
  )

  const mutationOpts = okText => ({
    onCompleted: () => {
      message.success(okText)
      refetchCurrent()
    },
    onError: e => message.error(e.message)
  })

  const [voidTransaction, { loading: voiding }] = useMutation(
    VOID_TRANSACTION,
    mutationOpts('Транзакция аннулирована')
  )
  const [linkTransaction, { loading: linking }] = useMutation(
    LINK_TRANSACTION,
    mutationOpts('Транзакции привязаны к пользователю')
  )
  const [addManualTransaction, { loading: creating }] = useMutation(
    ADD_MANUAL_TRANSACTION,
    mutationOpts('Чек создан')
  )

  return (
    <UsersTransactions
      transactions={transactions}
      loading={loading}
      filters={filters}
      onFiltersChange={handleFiltersChange}
      pagination={{ current: page, pageSize: limit, total }}
      onPaginationChange={handlePaginationChange}
      onExport={() =>
        exportTransactions({ variables: { filter: toFilterInput(filters) } })
      }
      exporting={exporting}
      onVoid={(transactionId, reason) =>
        voidTransaction({ variables: { transactionId, reason } })
      }
      onLink={(originalTransactionId, userId) =>
        linkTransaction({ variables: { originalTransactionId, userId } })
      }
      onCreateManual={values => addManualTransaction({ variables: values })}
      mutating={voiding || linking || creating}
    />
  )
}

export default WithMainLayout(UsersTransactionsContainer)
